import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { rewards as api, settings as settingsApi } from '@/api/endpoints'
import { Alert, Input, Spinner } from '@/components/ui'
import { useBootstrap } from '@/hooks/useBootstrap'
import { cn, formatDate } from '@/lib/utils'
import type { RewardType } from '@/types/api'

import RewardBonuses from './RewardBonuses'

/**
 * InstructorPresenter::actionSettings - the global reward rates.
 *
 * Switching a type on makes the matching calculator run on every
 * recalculation, so an instructor's pay is derived from their reservations
 * instead of being typed in by hand. Rates are in the school's currency.
 *
 * The keys are the legacy kebab-case names the calculators are registered
 * under (apps/rewards/calculators.py); a row whose key has no calculator is
 * a manual type and is not shown here.
 */
const AUTOMATIC_TYPES = [
  'hourly-rate',
  'bonus-extra-persons',
  'bonus-for-foreign-language',
  'own-car',
  'transporte-people',
] as const

export default function RewardsTab() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data: boot } = useBootstrap()

  const { data, isLoading } = useQuery({ queryKey: ['reward-types'], queryFn: api.types })
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  /** Local echo of the amount inputs so typing does not fight the query cache. */
  const [draft, setDraft] = useState<Record<number, string>>({})

  useEffect(() => {
    if (data) {
      setDraft(Object.fromEntries(data.map((row) => [row.id, String(row.reward ?? 0)])))
    }
  }, [data])

  const save = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<RewardType> }) =>
      api.saveType(id, payload),
    onSuccess: () => {
      setMessage({ kind: 'success', text: t('global.saved') })
      void qc.invalidateQueries({ queryKey: ['reward-types'] })
    },
    onError: (err) => setMessage({ kind: 'error', text: apiErrorMessage(err) }),
  })

  const recalculate = useMutation({
    mutationFn: () => api.recalculate({}),
    onSuccess: () => {
      setMessage({ kind: 'success', text: t('settings.rewardsRecalculated') })
      void qc.invalidateQueries({ queryKey: ['rewards'] })
    },
    onError: (err) => setMessage({ kind: 'error', text: apiErrorMessage(err) }),
  })

  if (isLoading || !data) {
    return (
      <div className="flex h-40 items-center justify-center text-slate-400">
        <Spinner className="h-5 w-5" />
      </div>
    )
  }

  const currency = boot?.currencies?.find((c) => c.id === boot.settings.currency_id)?.key ?? 'EUR'
  const systemLanguage = (boot?.settings.lang ?? 'sk').toUpperCase()

  const rows = AUTOMATIC_TYPES.map((key) => data.find((row) => row.type === key)).filter(
    (row): row is RewardType => Boolean(row),
  )

  return (
    <div className="max-w-4xl space-y-3">
      {message ? <Alert variant={message.kind}>{message.text}</Alert> : null}

      <p className="text-[13px] text-slate-500">{t('settings.rewardsIntro')}</p>

      <div className="card overflow-x-auto">
        <table className="w-full">
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className={cn(
                  'border-b border-white/70 align-top last:border-b-0',
                  row.allowed ? 'bg-green-50' : 'bg-slate-50',
                )}
              >
                <td className="w-48 px-3 py-2 text-[13px] font-semibold">
                  {t(`settings.rewardTypes.${row.type}.name`)}
                </td>
                <td className="px-3 py-2 text-[13px] text-slate-600">
                  {t(`settings.rewardTypes.${row.type}.hint`)}
                  {row.type === 'bonus-for-foreign-language' ? (
                    <>
                      {' '}
                      <strong className="font-medium text-slate-700">
                        {t('settings.rewardForeignHint', { lang: systemLanguage })}
                      </strong>
                    </>
                  ) : null}
                </td>
                <td className="w-44 whitespace-nowrap px-3 py-2">
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      className="w-20 text-right"
                      value={draft[row.id] ?? ''}
                      onChange={(e) => setDraft({ ...draft, [row.id]: e.target.value })}
                      onBlur={() => {
                        const next = Number.parseFloat(draft[row.id] ?? '')
                        if (Number.isNaN(next) || next < 0) {
                          setDraft({ ...draft, [row.id]: String(row.reward ?? 0) })
                          return
                        }
                        if (next === Number(row.reward)) return
                        setMessage(null)
                        // `reward` je v API reťazec (DecimalField), nie číslo.
                        save.mutate({ id: row.id, payload: { reward: String(next) } })
                      }}
                    />
                    <span className="text-[13px] font-medium text-slate-600">{currency}</span>
                  </div>
                  {/* The floor for automatic pricing - stamped when the rate
                      was first saved, see sql/010_reward_valid_from.sql. */}
                  <div className="mt-0.5 text-[11px] text-slate-500">
                    {row.valid_from
                      ? t('settings.rewardValidFrom', { date: formatDate(row.valid_from) })
                      : t('settings.rewardValidFromNever')}
                  </div>
                </td>
                <td className="w-32 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => {
                      setMessage(null)
                      save.mutate({ id: row.id, payload: { allowed: !row.allowed } })
                    }}
                    disabled={save.isPending}
                    className={cn(
                      'rounded px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-white',
                      row.allowed
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-slate-400 hover:bg-slate-500',
                    )}
                  >
                    {row.allowed ? t('settings.rewardAllowed') : t('settings.rewardDisabled')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <RewardBonuses
        bonuses={data.filter((row) => row.kind === 'custom').sort((a, b) => a.position - b.position || a.id - b.id)}
        currency={currency}
        onMessage={setMessage}
      />

      {/* Rates are "sticky": an instructor keeps the rate their first reward was
          calculated with, so a new rate only reaches past blocks on request. */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn-secondary"
          disabled={recalculate.isPending}
          onClick={() => {
            setMessage(null)
            recalculate.mutate()
          }}
        >
          {recalculate.isPending ? <Spinner /> : null}
          {t('settings.rewardsRecalculate')}
        </button>
        <span className="text-[12px] text-slate-500">{t('settings.rewardsStickyHint')}</span>
      </div>

      <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-600">
        {t('settings.rewardsSafetyHint')}
      </div>

      <PayslipPrefix onMessage={setMessage} />
    </div>
  )
}

/** Prefix radu výplatných listov (`settings.payslip_prefix`, `sql/039`). */
function PayslipPrefix({
  onMessage,
}: {
  onMessage: (message: { kind: 'success' | 'error'; text: string } | null) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data: boot } = useBootstrap()
  const current = boot?.settings.payslip_prefix ?? 'VL'
  const [value, setValue] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (prefix: string) => settingsApi.update({ payslip_prefix: prefix }),
    onSuccess: () => {
      onMessage({ kind: 'success', text: t('global.saved') })
      void qc.invalidateQueries({ queryKey: ['bootstrap'] })
    },
    onError: (err) => onMessage({ kind: 'error', text: apiErrorMessage(err) }),
  })
  const shown = value ?? current
  return (
    <section className="card flex flex-wrap items-end gap-3 p-3">
      <label className="block">
        <span className="label">{t('settings.payslipPrefix')}</span>
        <Input
          className="w-28"
          maxLength={10}
          placeholder="VL"
          value={shown}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => {
            const next = shown.trim()
            if (next && next !== current) save.mutate(next)
          }}
        />
      </label>
      <p className="pb-2 text-xs text-slate-500">
        {t('settings.payslipPrefixHint')}{' '}
        <strong className="font-medium text-slate-700">
          {(shown.trim() || 'VL') + String(new Date().getFullYear()) +
            String(1).padStart(boot?.settings.receipt_number_length ?? 5, '0')}
        </strong>
      </p>
    </section>
  )
}
